import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { shortenUrl, isValidUrl, ShortenedUrlResponse, UrlMapping, buildShortUrl, buildQrCodeUrl } from '../../utils/sqrone-api';

interface UrlShortenerProps {
  onUrlShortened: (url: ShortenedUrlResponse) => void;
}

export function UrlShortener({ onUrlShortened }: UrlShortenerProps) {
  const { token } = useAuth();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate name (FS8: mandatory)
      if (!name.trim()) {
        toast.error('Please enter a name for this checkpoint');
        setLoading(false);
        return;
      }

      // Validate URL
      if (!isValidUrl(url)) {
        toast.error('Please enter a valid URL (including http:// or https://)');
        setLoading(false);
        return;
      }

      // Create checkpoint via API (works for both authenticated and anonymous users)
      const data: UrlMapping = await shortenUrl(url, name.trim(), null, token);

      // Construct shortUrl and qrCodeUrl using utility functions
      const shortUrl = buildShortUrl(data.shortCode, data.domain, data.sslEnabled);
      const qrCodeUrl = buildQrCodeUrl(data.shortCode, data.domain, data.sslEnabled);

      const enrichedData: ShortenedUrlResponse = {
        originalUrl: data.originalUrl,
        shortUrl,
        shortCode: data.shortCode,
        qrCodeUrl,
        name: name.trim(),
        isActive: data.isActive ?? true,
        id: data.id,
      };

      onUrlShortened(enrichedData);
      toast.success('Checkpoint created!');

      // Reset form
      setName('');
      setUrl('');

    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
      {/* <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold">Create Checkpoint</CardTitle>
      </CardHeader> */}
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name field */}
          <div className="space-y-2">
            {/* <Label htmlFor="name" className="text-sm font-medium">
              Name <span className="text-red-500">*</span>
            </Label> */}
            <Input
              id="name"
              type="text"
              placeholder="Give your checkpoint a friendly name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10"
              maxLength={255}
            />
          </div>

          {/* Destination URL field */}
          <div className="space-y-2">
            {/* <Label htmlFor="url" className="text-sm font-medium">
              Destination URL <span className="text-red-500">*</span>
            </Label> */}
            <Input
              id="url"
              type="text"
              placeholder="Destination (page, form, instructions, or action)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-10 font-mono text-sm"
            />
          </div>

          {/* Submit button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 mr-2" />
                Create Checkpoint
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
